<template>
  <a-modal
    :visible="visible"
    :footer="null"
    :centered="true"
    :width="760"
    wrapClassName="am35-rotate-angle-modal-wrap"
    :maskStyle="{ background: 'rgba(8, 8, 12, 0.82)' }"
    @cancel="closeModal"
  >
    <div class="am35-rotate-angle-modal">
      <div class="am35-rotate-angle-title">
        {{ $t("mouse.rotateAngle") }}
      </div>
      <div class="am35-rotate-angle-switch-row">
        <span class="am35-rotate-angle-switch-label">
          {{ $t("mouse.rotation") }}
        </span>
        <md-switch
          class="md-primary"
          v-model="rotateAngleEnabled"
        ></md-switch>
      </div>
      <div class="am35-rotate-angle-intro">
        {{ $t("mouse.rotateAngleTooltip") }}
      </div>
      <div class="am35-rotate-angle-preview">
        <div class="am35-rotate-angle-crosshair horizontal"></div>
        <div class="am35-rotate-angle-crosshair vertical"></div>
        <img
          v-if="previewImageReady && previewImageUrl"
          class="am35-rotate-angle-mouse-shell"
          :class="{ sliding: isRotateAngleSliding }"
          :src="previewImageUrl"
          alt="AM35 rotate angle preview"
          :style="rotateAnglePreviewStyle"
        />
      </div>
      <div class="am35-rotate-angle-slider">
        <div class="am35-rotate-angle-value">{{ previewRotateAngle }}</div>
        <a-slider
          class="slider rotate-angle-slider"
          :value="previewRotateAngle"
          :min="-90"
          :max="90"
          :disabled="!rotateAngleEnabled"
          :tooltipVisible="false"
          @change="onRotateAngleSliderChange"
          @afterChange="onRotateAngleSliderAfterChange"
        />
        <div class="am35-rotate-angle-scale">
          <span class="scale-left">-90</span>
          <span class="scale-center">0</span>
          <span class="scale-right">90</span>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { mapState } from "vuex";
import { parseAM35SN, getAM35MianImageKey } from "@/utils/businessUtils";

const ROTATE_ANGLE_PROTOCOL_DEBOUNCE_MS = 120;

function normalizeRotateAngleValue(value) {
  const angle = Number(value);
  if (Number.isNaN(angle)) {
    return 0;
  }
  return Math.max(-90, Math.min(90, angle));
}

function buildRotateAnglePayload(vm, partial = {}) {
  return {
    AM35rotateAngleSwitch:
      partial.AM35rotateAngleSwitch !== undefined
        ? Number(partial.AM35rotateAngleSwitch ? 1 : 0)
        : Number(vm.AM35rotateAngleSwitch ? 1 : 0),
    setAM35rotateAngle:
      partial.setAM35rotateAngle !== undefined
        ? normalizeRotateAngleValue(partial.setAM35rotateAngle)
        : normalizeRotateAngleValue(vm.setAM35rotateAngle),
  };
}

function dispatchRotateAngleStore(vm, payload) {
  vm.$store.dispatch("mouseAM35/setAM35RotateAngle", payload);
}

function clearRotateAngleProtocolTimer(vm) {
  if (vm.rotateAngleProtocolTimer) {
    clearTimeout(vm.rotateAngleProtocolTimer);
    vm.rotateAngleProtocolTimer = null;
  }
}

function requestRotateAngleProtocol(vm, payload, { immediate = false } = {}) {
  clearRotateAngleProtocolTimer(vm);
  if (immediate) {
    vm.$store.dispatch("loader/setAM35rotateAngle", payload);
    return;
  }

  vm.rotateAngleProtocolTimer = setTimeout(() => {
    vm.rotateAngleProtocolTimer = null;
    vm.$store.dispatch("loader/setAM35rotateAngle", payload);
  }, ROTATE_ANGLE_PROTOCOL_DEBOUNCE_MS);
}

export default {
  name: "RotationModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      rotateAngleProtocolTimer: null,
      previewImageUrl: "",
      previewImageReady: false,
      rotateAnglePreviewLoadToken: 0,
      liveRotateAngle: 0,
      isRotateAngleSliding: false,
    };
  },
  watch: {
    rotateAngleImage: {
      immediate: true,
      handler(imageUrl) {
        this.preloadRotateAngleImage(imageUrl);
      },
    },
    visible: {
      immediate: true,
      handler(isVisible) {
        if (!isVisible) {
          this.isRotateAngleSliding = false;
        }
        this.syncPreviewRotateAngle(this.setAM35rotateAngle);
      },
    },
    setAM35rotateAngle(value) {
      if (this.isRotateAngleSliding) {
        return;
      }
      this.syncPreviewRotateAngle(value);
    },
  },
  computed: {
    ...mapState("assets", {
      images: "images",
    }),
    ...mapState("loader", {
      currentDevice: "currentDevice",
    }),
    ...mapState("mouseAM35", {
      AM35rotateAngleSwitch: "AM35rotateAngleSwitch",
      setAM35rotateAngle: "setAM35rotateAngle",
    }),
    rotateAngleImage() {
      const { code, MouseSn } = this.currentDevice || {};
      const snInfo = parseAM35SN(MouseSn);
      const codeImageMap = {
        am35: getAM35MianImageKey(snInfo),
        am35_d: getAM35MianImageKey(snInfo),
      };
      const defaultImageKey = "device_am35_m_b";
      return (
        this.images?.[codeImageMap[code] || defaultImageKey] ||
        this.images?.[defaultImageKey] ||
        this.images?.device_am35_m_a ||
        ""
      );
    },
    previewRotateAngle() {
      return normalizeRotateAngleValue(this.liveRotateAngle);
    },
    rotateAngleEnabled: {
      get() {
        return Boolean(this.AM35rotateAngleSwitch);
      },
      set(value) {
        const payload = buildRotateAnglePayload(this, {
          AM35rotateAngleSwitch: value ? 1 : 0,
        });
        dispatchRotateAngleStore(this, payload);
        requestRotateAngleProtocol(this, payload, { immediate: true });
      },
    },
    rotateAnglePreviewStyle() {
      return {
        transform: `translate(-50%, -50%) rotate(${this.previewRotateAngle}deg)`,
      };
    },
  },
  methods: {
    syncPreviewRotateAngle(value) {
      this.liveRotateAngle = normalizeRotateAngleValue(value);
    },
    preloadRotateAngleImage(imageUrl) {
      const nextLoadToken = this.rotateAnglePreviewLoadToken + 1;
      this.rotateAnglePreviewLoadToken = nextLoadToken;
      this.previewImageReady = false;
      this.previewImageUrl = "";

      if (!imageUrl) {
        return;
      }

      const previewImage = new Image();
      previewImage.src = imageUrl;

      if (previewImage.complete) {
        if (this.rotateAnglePreviewLoadToken === nextLoadToken) {
          this.previewImageUrl = imageUrl;
          this.previewImageReady = true;
        }
        return;
      }

      // 等图片真正解码完成后再显示，避免弹窗首帧偶发空白。
      previewImage.onload = () => {
        if (this.rotateAnglePreviewLoadToken !== nextLoadToken) {
          return;
        }
        this.previewImageUrl = imageUrl;
        this.previewImageReady = true;
      };

      previewImage.onerror = () => {
        if (this.rotateAnglePreviewLoadToken !== nextLoadToken) {
          return;
        }
        this.previewImageUrl = imageUrl;
        this.previewImageReady = true;
      };
    },
    closeModal() {
      this.isRotateAngleSliding = false;
      this.syncPreviewRotateAngle(this.setAM35rotateAngle);
      this.$emit("update:visible", false);
    },
    onRotateAngleSliderChange(value) {
      const nextAngle = normalizeRotateAngleValue(value);
      this.isRotateAngleSliding = true;
      this.syncPreviewRotateAngle(nextAngle);
      const payload = buildRotateAnglePayload(this, {
        setAM35rotateAngle: nextAngle,
      });
      dispatchRotateAngleStore(this, payload);
      requestRotateAngleProtocol(this, payload);
    },
    onRotateAngleSliderAfterChange(value) {
      const nextAngle = normalizeRotateAngleValue(value);
      this.syncPreviewRotateAngle(nextAngle);
      const payload = buildRotateAnglePayload(this, {
        setAM35rotateAngle: nextAngle,
      });
      dispatchRotateAngleStore(this, payload);
      this.isRotateAngleSliding = false;
      requestRotateAngleProtocol(this, payload, { immediate: true });
    },
  },
  beforeDestroy() {
    clearRotateAngleProtocolTimer(this);
  },
};
</script>

<style lang="scss" scoped>
.am35-rotate-angle-modal {
  color: #ffffff;
  padding: 18px 12px 8px;

  .am35-rotate-angle-title {
    font-size: 28px;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 18px;
  }

  .am35-rotate-angle-switch-row {
    display: flex;
    align-items: center;
    gap: 12px;
    color: $primary-color;
    font-size: 20px;
    margin-bottom: 16px;
  }

  .am35-rotate-angle-intro {
    width: 100%;
    font-size: 16px;
    line-height: 1.75;
    color: #d4d4de;
    margin-bottom: 28px;
  }

  .am35-rotate-angle-preview {
    position: relative;
    width: 320px;
    height: 230px;
    margin: 0 auto 30px;
  }

  .am35-rotate-angle-crosshair {
    position: absolute;
    background: rgba(255, 255, 255, 0.2);

    &.horizontal {
      left: 26px;
      right: 26px;
      top: 50%;
      height: 1px;
      transform: translateY(-50%);
    }

    &.vertical {
      top: 10px;
      bottom: 10px;
      left: 50%;
      width: 1px;
      transform: translateX(-50%);
    }
  }

  .am35-rotate-angle-mouse-shell {
    position: absolute;
    top: 50%;
    left: 50%;
    width: auto;
    height: 66%;
    max-width: 100%;
    transition: transform 0.12s ease-out;
    transform-origin: center;
    will-change: transform;
    backface-visibility: hidden;
    pointer-events: none;
    user-select: none;

    &.sliding {
      transition: none;
    }
  }

  .am35-rotate-angle-slider {
    width: 100%;
    max-width: 620px;
    margin: 0 auto;
  }

  .am35-rotate-angle-value {
    width: 44px;
    height: 32px;
    line-height: 32px;
    margin: 0 auto 16px;
    text-align: center;
    font-size: 16px;
    color: $primary-color;
    border: 1px solid rgba(191, 148, 255, 0.65);
    border-radius: 6px;
    background: rgba(22, 16, 30, 0.92);
  }

  .am35-rotate-angle-scale {
    position: relative;
    height: 24px;
    margin-top: 6px;
    font-size: 16px;
    color: #cfcfe1;

    span {
      position: absolute;
      top: 0;
      transform: translateX(-50%);
    }

    .scale-left {
      left: 0;
      transform: translateX(0);
    }

    .scale-center {
      left: 50%;
    }

    .scale-right {
      left: 100%;
    }
  }
}
</style>

<style lang="scss">
.am35-rotate-angle-modal-wrap {
  .ant-modal-content {
    background: #0c0c10;
    border: 1px solid rgba(191, 148, 255, 0.28);
    border-radius: 16px;
    box-shadow: 0 24px 80px rgba(73, 41, 132, 0.42);
  }

  .ant-modal-close {
    color: #ffffff;
  }

  .ant-modal-body {
    padding: 22px 26px 28px;
  }

  .rotate-angle-slider {
    padding: 4px 0;

    .ant-slider-rail {
      height: 4px;
      background-color: #4c3a61 !important;
    }

    .ant-slider-track {
      height: 4px;
      background-color: $primary-color !important;
    }

    .ant-slider-step {
      height: 4px;
    }

    .ant-slider-handle {
      width: 20px;
      height: 20px;
      margin-top: -7px;
      background-color: $primary-color !important;
      border: 0;
      box-shadow: none;
    }
  }

  .md-switch.md-theme-default.md-checked.md-primary .md-switch-container {
    background-color: $primary-color;
  }

  .rotate-angle-slider.ant-slider-disabled {
    .ant-slider-track {
      background-color: rgba(191, 148, 255, 0.38) !important;
    }

    .ant-slider-handle {
      background-color: rgba(191, 148, 255, 0.38) !important;
    }
  }
}
</style>
